import React from 'react'

import { cn } from '@/utils/cn'

export interface AvatarProps {
  /**
   * Nombre completo del usuario para extraer las iniciales
   */
  name?: string | null

  /**
   * URL de la imagen del avatar (para futuro uso)
   */
  imageUrl?: string

  /**
   * Tamaño del avatar
   */
  size?: 'sm' | 'md' | 'lg' | 'xl'

  /**
   * Clases CSS adicionales
   */
  className?: string

  /**
   * Color de fondo personalizado
   */
  bgColor?: string

  /**
   * Color del texto personalizado
   */
  textColor?: string
}

const sizeClasses = {
  sm: 'h-6 w-6 text-xs',
  md: 'h-8 w-8 text-sm',
  lg: 'h-10 w-10 text-base',
  xl: 'h-12 w-12 text-lg'
}

/**
 * Extrae las iniciales del nombre completo
 * Toma la primera letra del nombre y la primera letra del apellido
 * Si solo hay un nombre, toma las dos primeras letras
 */
const getInitials = (name?: string | null): string => {
  if (!name) return 'U'

  const trimmedName = name.trim()
  if (!trimmedName) return 'U'

  const parts = trimmedName.split(' ').filter((part) => part.length > 0)

  if (parts.length === 0) return 'U'
  if (parts.length === 1) {
    // Si solo hay una palabra, tomar las dos primeras letras (o una si es muy corta)
    const word = parts[0]
    return word.length >= 2 ? `${word[0]}${word[1]}`.toUpperCase() : word[0].toUpperCase()
  }

  // Tomar primera letra del nombre y primera letra del apellido
  const firstName = parts[0]
  const lastName = parts[parts.length - 1]
  return `${firstName[0]}${lastName[0]}`.toUpperCase()
}

export const Avatar: React.FC<AvatarProps> = ({
  name,
  imageUrl,
  size = 'md',
  className,
  bgColor = 'bg-blue-600',
  textColor = 'text-white'
}) => {
  const initials = getInitials(name)

  // Preparado para futuro uso de imágenes
  if (imageUrl) {
    return (
      <div
        className={cn(
          'rounded-full overflow-hidden flex items-center justify-center',
          sizeClasses[size],
          className
        )}
      >
        <img src={imageUrl} alt={name || 'Avatar'} className="h-full w-full object-cover" />
      </div>
    )
  }

  return (
    <div
      className={cn(
        'rounded-full flex items-center justify-center font-medium',
        sizeClasses[size],
        bgColor,
        textColor,
        className
      )}
      aria-label={`Avatar de ${name || 'Usuario'}`}
    >
      <span>{initials}</span>
    </div>
  )
}
